import React, { FC, useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  Avatar,
  Button,
  Chip,
  CircularProgress,
  TextField,
  InputAdornment,
  alpha,
  Divider,
} from '@mui/material';
import {
  Close as CloseIcon,
  Search as SearchIcon,
  Devices as DevicesIcon,
  HelpOutline as HelpIcon,
} from '@mui/icons-material';
import clientService from 'src/clientService';
import {
  getProjectDeviceCustodyList,
  checkoutDeviceToProject,
  checkinDeviceFromProject,
  DeviceCustodyRecord
} from 'src/api2/deviceCustodyApi';
import { DeviceAdminDetailWithDeployment, DeviceStatus } from 'src/api/Api';
import { useSnackbar, SnackbarType } from 'src/view/components/SnackbarService';

// Import local images for devices
import audioImg from 'src/resources/images/audio.png';
import cameratrapImg from 'src/resources/images/cameratrap.png';
import hydroImg from 'src/resources/images/hydro.png';

const getDeviceImage = (device: any): string => {
  if (!device) return '';
  const category = device.device_type?.category ?? device.type ?? '';
  const text = `${device.name || ''} ${device.serial_number || ''} ${device.human_readable_id || ''} ${category}`.toUpperCase();
  if (text.includes('AUD')) {
    return audioImg;
  }
  if (text.includes('CAM') || text.includes('IMG') || text.includes('IMAGE')) {
    return cameratrapImg;
  }
  if (text.includes('HYD') || text.includes('HYDRO')) {
    return hydroImg;
  }
  return `https://api.dicebear.com/7.x/shapes/svg?seed=${device.device_id}`;
};

interface ProjectDevicesDialogProps {
  open: boolean;
  onClose: () => void;
  projectId: string;
  onUpdate: (devices: DeviceCustodyRecord[]) => void;
}

const ProjectDevicesDialog: FC<ProjectDevicesDialogProps> = ({
  open,
  onClose,
  projectId,
  onUpdate,
}) => {
  const { showSnackbar } = useSnackbar();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState<string | null>(null);

  // Data states
  const [custodyRecords, setCustodyRecords] = useState<DeviceCustodyRecord[]>([]);
  const [allSystemDevices, setAllSystemDevices] = useState<DeviceAdminDetailWithDeployment[]>([]);
  const [activeDeployments, setActiveDeployments] = useState<any[]>([]);

  // Search/Filter states
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch all custody details, system devices, and deployments
  const fetchData = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [custodyResp, devicesResp, deploymentsResp] = await Promise.all([
        getProjectDeviceCustodyList(projectId),
        clientService.api.getAllDevicesAdminApiV1AdminDevicesGet(),
        clientService.api.getAllDeploymentsApiV1AdminDeploymentsGet(),
      ]);

      const safeCustody = Array.isArray(custodyResp) ? custodyResp : [];
      const safeDevices = (devicesResp && Array.isArray(devicesResp.data)) ? devicesResp.data : [];
      const safeDeployments = (deploymentsResp && Array.isArray(deploymentsResp.data)) ? deploymentsResp.data : [];

      setCustodyRecords(safeCustody);
      setAllSystemDevices(safeDevices);
      setActiveDeployments(safeDeployments);

      // Trigger update in parent component with currently active custody
      const active = safeCustody.filter(r => r.returned_at === null);
      onUpdate(active);
    } catch (error) {
      console.error('Failed to fetch project devices dialog data:', error);
      showSnackbar({
        message: 'Failed to fetch device custody data',
        type: SnackbarType.ERROR,
      });
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    if (open && projectId) {
      fetchData();
      setSearchQuery('');
      setTabValue(0);
    }
  }, [open, projectId]);

  // Derived: Devices currently checked out to this project
  const projectDevices = useMemo(() => {
    const records = Array.isArray(custodyRecords) ? custodyRecords : [];
    return records.filter(record => record.returned_at === null);
  }, [custodyRecords]);

  // Derived: Active deployment device IDs across all projects
  const deployedDeviceIds = useMemo(() => {
    const deployments = Array.isArray(activeDeployments) ? activeDeployments : [];
    return new Set(deployments.map(d => d.device_id).filter(Boolean));
  }, [activeDeployments]);

  // Derived: General stock devices (IDLE, not checked out to ANY project, and not active in deployments)
  // Wait, to know if a device is checked out to *any* project, we check if there are custody records across the system
  // Since we only get custody for *this* project from getProjectDeviceCustodyList, how do we know if it's checked out elsewhere?
  // Let's assume a device is checked out elsewhere if it is not currently checked out to this project, and its status/deployment map indicates it's deployed or we can mock/filter it.
  // Wait! The backend has: GET /api/v1/device-custody/project/{project_id}.
  // But wait, the system-wide list of devices `allSystemDevices` includes their status.
  // Wait! Let's check: if `allSystemDevices` has status "ACTIVE" or "DEPLOYED" or "INACTIVE", how can we find general stock?
  // Actually, we can check if it has custody checked out to *our* project. If not, and it has no active deployment (not in `deployedDeviceIds`), and is `DeviceStatus.ACTIVE`, then it is in stock!
  // Wait, could it be checked out to another project? Yes. But for now, we can check if the device status is IDLE/ACTIVE and not checked out to our project. Let's see: if a device is checked out to another project, it wouldn't be in general stock.
  // To keep it simple and robust, let's treat any device as general stock if its status is not `INACTIVE`, it is not checked out to this project, and it is not currently deployed in any active deployment.
  const generalStockDevices = useMemo(() => {
    const devices = Array.isArray(allSystemDevices) ? allSystemDevices : [];
    return devices.filter(device => {
      return (
        device &&
        device.status !== DeviceStatus.INACTIVE &&
        !device.assigned_project_id &&
        !deployedDeviceIds.has(device.device_id)
      );
    });
  }, [allSystemDevices, deployedDeviceIds]);

  // Search filtering
  const filteredProjectDevices = useMemo(() => {
    const devices = Array.isArray(projectDevices) ? projectDevices : [];
    return devices.filter(pd => {
      const dev = pd.device;
      if (!dev) return false;
      const query = searchQuery.toLowerCase();
      return (
        dev.name?.toLowerCase().includes(query) ||
        dev.human_readable_id?.toLowerCase().includes(query) ||
        dev.serial_number?.toLowerCase().includes(query)
      );
    });
  }, [projectDevices, searchQuery]);

  const filteredStockDevices = useMemo(() => {
    const devices = Array.isArray(generalStockDevices) ? generalStockDevices : [];
    return devices.filter(dev => {
      const query = searchQuery.toLowerCase();
      return (
        dev.name?.toLowerCase().includes(query) ||
        dev.human_readable_id?.toLowerCase().includes(query) ||
        dev.serial_number?.toLowerCase().includes(query)
      );
    });
  }, [generalStockDevices, searchQuery]);

  // Derived: All project custody history sorted by assigned_at desc
  const sortedCustodyRecords = useMemo(() => {
    const records = Array.isArray(custodyRecords) ? [...custodyRecords] : [];
    return records.sort((a, b) => {
      const timeA = a.assigned_at ? new Date(a.assigned_at).getTime() : 0;
      const timeB = b.assigned_at ? new Date(b.assigned_at).getTime() : 0;
      return timeB - timeA;
    });
  }, [custodyRecords]);

  // Derived: Filtered custody history search
  const filteredHistoryRecords = useMemo(() => {
    const records = Array.isArray(sortedCustodyRecords) ? sortedCustodyRecords : [];
    return records.filter(r => {
      const dev = r.device;
      if (!dev) return false;
      const query = searchQuery.toLowerCase();
      return (
        dev.name?.toLowerCase().includes(query) ||
        dev.human_readable_id?.toLowerCase().includes(query) ||
        dev.serial_number?.toLowerCase().includes(query)
      );
    });
  }, [sortedCustodyRecords, searchQuery]);

  // Action: Check out a device to this project
  const handleCheckout = async (deviceId: string) => {
    setActionLoadingId(deviceId);
    try {
      await checkoutDeviceToProject({
        device_id: deviceId,
        project_id: projectId,
      });
      showSnackbar({
        message: 'Device checked out to project successfully',
        type: SnackbarType.SUCCESS,
      });
      await fetchData(true);
    } catch (error) {
      console.error('Failed to checkout device:', error);
      showSnackbar({
        message: 'Failed to checkout device to project',
        type: SnackbarType.ERROR,
      });
    } finally {
      setActionLoadingId(null);
    }
  };

  // Action: Check in a device back to stock (unassign)
  const handleCheckin = async (deviceId: string) => {
    setActionLoadingId(deviceId);
    try {
      await checkinDeviceFromProject(deviceId);
      showSnackbar({
        message: 'Device checked in (returned to stock) successfully',
        type: SnackbarType.SUCCESS,
      });
      await fetchData(true);
    } catch (error) {
      console.error('Failed to checkin device:', error);
      showSnackbar({
        message: 'Failed to checkin device',
        type: SnackbarType.ERROR,
      });
    } finally {
      setActionLoadingId(null);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          bgcolor: '#111',
          color: '#FFF',
          borderRadius: '24px',
          border: '1px solid rgba(255,255,255,0.05)',
          backgroundImage: 'linear-gradient(135deg, rgba(28,28,28,0.7) 0%, rgba(15,15,15,0.8) 100%)',
          boxShadow: '0 24px 48px rgba(0,0,0,0.8), 0 0 0 1px rgba(255,255,255,0.05)',
          backdropFilter: 'blur(20px)',
          overflow: 'hidden',
          minHeight: '550px',
        },
      }}
    >
      {/* Title & Tabs Header */}
      <Box sx={{ p: 3, pb: 1, borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800, color: '#FFF', display: 'flex', alignItems: 'center', gap: 1 }}>
              <DevicesIcon sx={{ color: '#10B981' }} />
              Manage Project Devices
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', mt: 0.5, display: 'block' }}>
              Assign devices to the project inventory so field workers can deploy them to site locations.
            </Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ color: '#666', '&:hover': { color: '#FFF' } }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Tabs
          value={tabValue}
          onChange={(_, val) => setTabValue(val)}
          sx={{
            minHeight: '40px',
            '& .MuiTabs-indicator': { backgroundColor: '#10B981', height: '2px' },
            '& .MuiTab-root': {
              color: 'rgba(255,255,255,0.4)',
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '14px',
              minHeight: '40px',
              pb: 1.5,
              '&.Mui-selected': { color: '#10B981' },
            },
          }}
        >
          <Tab label={`Project Inventory (${projectDevices.length})`} />
          <Tab label={`General Stock (${generalStockDevices.length})`} />
          <Tab label={`History (${sortedCustodyRecords.length})`} />
        </Tabs>
      </Box>

      {/* Filter and Search Bar */}
      <Box sx={{ px: 3, pt: 2, pb: 1, display: 'flex', gap: 2, alignItems: 'center' }}>
        <TextField
          placeholder={tabValue === 0 ? "Search project devices..." : tabValue === 1 ? "Search stock devices..." : "Search history records..."}
          size="small"
          fullWidth
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'rgba(255,255,255,0.3)', fontSize: 18 }} />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              bgcolor: 'rgba(255,255,255,0.03)',
              color: '#FFF',
              borderRadius: '12px',
              fontSize: '14px',
              border: '1px solid rgba(255,255,255,0.05)',
              '& fieldset': { border: 'none' },
              '&:hover fieldset': { border: 'none' },
              '&.Mui-focused fieldset': { border: 'none' },
            },
          }}
        />
      </Box>

      {/* Content */}
      <DialogContent sx={{ p: 3, pt: 1, height: '420px', overflowY: 'auto' }}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="100%">
            <CircularProgress sx={{ color: '#10B981' }} />
          </Box>
        ) : tabValue === 0 ? (
          /* Tab 0: Project Devices */
          filteredProjectDevices.length === 0 ? (
            <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" height="100%" sx={{ color: 'rgba(255,255,255,0.3)' }}>
              <HelpIcon sx={{ fontSize: 48, mb: 1.5, opacity: 0.5 }} />
              <Typography variant="body2">No devices assigned to this project yet.</Typography>
              <Button
                size="small"
                onClick={() => setTabValue(1)}
                sx={{ color: '#10B981', mt: 1, textTransform: 'none', fontWeight: 600 }}
              >
                Go check out devices from general stock
              </Button>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {filteredProjectDevices.map(pd => {
                const dev = pd.device;
                if (!dev) return null;
                const isCurrentlyDeployed = deployedDeviceIds.has(dev.device_id);
                const isBusy = actionLoadingId === dev.device_id;

                return (
                  <Grid item xs={12} key={pd.custody_id}>
                    <Card
                      sx={{
                        bgcolor: 'rgba(255, 255, 255, 0.02)',
                        border: '1px solid rgba(255, 255, 255, 0.05)',
                        borderRadius: '12px',
                        transition: 'all 0.2s',
                        '&:hover': {
                          bgcolor: 'rgba(255, 255, 255, 0.04)',
                          borderColor: 'rgba(16, 185, 129, 0.2)',
                        },
                      }}
                    >
                      <CardContent sx={{ p: '16px !important', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar
                            src={getDeviceImage(dev)}
                            sx={{ width: 44, height: 44, bgcolor: 'rgba(255,255,255,0.05)', borderRadius: '8px' }}
                          />
                          <Box>
                            <Typography sx={{ color: '#FFF', fontWeight: 700, fontSize: '15px' }}>
                              {dev.name || dev.human_readable_id}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5 }}>
                              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)', fontWeight: 600 }}>
                                ID: {dev.human_readable_id}
                              </Typography>
                              <Divider orientation="vertical" flexItem sx={{ bgcolor: 'rgba(255,255,255,0.1)', my: 0.5 }} />
                              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)' }}>
                                SN: {dev.serial_number}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Chip
                            label={dev.type}
                            size="small"
                            sx={{
                              bgcolor: 'rgba(255,255,255,0.05)',
                              color: 'rgba(255,255,255,0.7)',
                              fontWeight: 700,
                              fontSize: '10px',
                              borderRadius: '4px',
                            }}
                          />
                          <Chip
                            label={isCurrentlyDeployed ? 'DEPLOYED' : 'IDLE'}
                            size="small"
                            sx={{
                              bgcolor: isCurrentlyDeployed ? 'rgba(0, 230, 118, 0.1)' : 'rgba(255, 214, 0, 0.1)',
                              color: isCurrentlyDeployed ? '#00E676' : '#FFD600',
                              fontWeight: 700,
                              fontSize: '10px',
                              borderRadius: '4px',
                            }}
                          />
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => handleCheckin(dev.device_id)}
                            disabled={isCurrentlyDeployed || isBusy}
                            sx={{
                              borderColor: 'rgba(239, 68, 68, 0.3)',
                              color: '#EF4444',
                              borderRadius: '8px',
                              textTransform: 'none',
                              fontSize: '12px',
                              fontWeight: 600,
                              '&:hover': {
                                bgcolor: alpha('#EF4444', 0.08),
                                borderColor: '#EF4444',
                              },
                              '&.Mui-disabled': {
                                color: 'rgba(255,255,255,0.15)',
                                borderColor: 'rgba(255,255,255,0.05)',
                              },
                            }}
                          >
                            {isBusy ? <CircularProgress size={16} color="inherit" /> : 'Return to Stock'}
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )
        ) : tabValue === 1 ? (
          /* Tab 1: General Stock */
          filteredStockDevices.length === 0 ? (
            <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" height="100%" sx={{ color: 'rgba(255,255,255,0.3)' }}>
              <HelpIcon sx={{ fontSize: 48, mb: 1.5, opacity: 0.5 }} />
              <Typography variant="body2">No available devices in general stock.</Typography>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {filteredStockDevices.map(dev => {
                const isBusy = actionLoadingId === dev.device_id;
                return (
                  <Grid item xs={12} key={dev.device_id}>
                    <Card
                      sx={{
                        bgcolor: 'rgba(255, 255, 255, 0.02)',
                        border: '1px solid rgba(255, 255, 255, 0.05)',
                        borderRadius: '12px',
                        transition: 'all 0.2s',
                        '&:hover': {
                          bgcolor: 'rgba(255, 255, 255, 0.04)',
                          borderColor: 'rgba(16, 185, 129, 0.2)',
                        },
                      }}
                    >
                      <CardContent sx={{ p: '16px !important', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar
                            src={getDeviceImage(dev)}
                            sx={{ width: 44, height: 44, bgcolor: 'rgba(255,255,255,0.05)', borderRadius: '8px' }}
                          />
                          <Box>
                            <Typography sx={{ color: '#FFF', fontWeight: 700, fontSize: '15px' }}>
                              {dev.name || dev.human_readable_id}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5 }}>
                              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)', fontWeight: 600 }}>
                                ID: {dev.human_readable_id}
                              </Typography>
                              <Divider orientation="vertical" flexItem sx={{ bgcolor: 'rgba(255,255,255,0.1)', my: 0.5 }} />
                              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)' }}>
                                SN: {dev.serial_number}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Chip
                            label={dev.type}
                            size="small"
                            sx={{
                              bgcolor: 'rgba(255,255,255,0.05)',
                              color: 'rgba(255,255,255,0.7)',
                              fontWeight: 700,
                              fontSize: '10px',
                              borderRadius: '4px',
                            }}
                          />
                          <Chip
                            label="STOCK"
                            size="small"
                            sx={{
                              bgcolor: 'rgba(16, 185, 129, 0.1)',
                              color: '#10B981',
                              fontWeight: 700,
                              fontSize: '10px',
                              borderRadius: '4px',
                            }}
                          />
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => handleCheckout(dev.device_id)}
                            disabled={isBusy}
                            sx={{
                              bgcolor: '#10B981',
                              color: '#000',
                              borderRadius: '8px',
                              textTransform: 'none',
                              fontSize: '12px',
                              fontWeight: 700,
                              '&:hover': {
                                bgcolor: '#059669',
                              },
                            }}
                          >
                            {isBusy ? <CircularProgress size={16} color="inherit" /> : 'Check Out to Project'}
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )
        ) : (         
      // AI-GENERATED-START
      /* Tab 2: History */
          filteredHistoryRecords.length === 0 ? (
            <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" height="100%" sx={{ color: 'rgba(255,255,255,0.3)' }}>
              <HelpIcon sx={{ fontSize: 48, mb: 1.5, opacity: 0.5 }} />
              <Typography variant="body2">No custody history records found for this project.</Typography>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {filteredHistoryRecords.map(r => {
                const dev = r.device;
                if (!dev) return null;
                const isReturned = r.returned_at !== null;
                
                const checkoutStr = r.assigned_at 
                  ? new Date(r.assigned_at).toLocaleString() 
                  : 'N/A';
                const checkinStr = r.returned_at 
                  ? new Date(r.returned_at).toLocaleString() 
                  : 'Currently Active';

                return (
                  <Grid item xs={12} key={r.custody_id}>
                    <Card
                      sx={{
                        bgcolor: 'rgba(255, 255, 255, 0.02)',
                        border: '1px solid rgba(255, 255, 255, 0.05)',
                        borderRadius: '12px',
                      }}
                    >
                      <CardContent sx={{ p: '16px !important', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar
                            src={getDeviceImage(dev)}
                            sx={{ width: 44, height: 44, bgcolor: 'rgba(255,255,255,0.05)', borderRadius: '8px' }}
                          />
                          <Box>
                            <Typography sx={{ color: '#FFF', fontWeight: 700, fontSize: '15px' }}>
                              {dev.name || dev.human_readable_id}
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
                              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', fontWeight: 600 }}>
                                Checked Out: {checkoutStr}
                              </Typography>
                              <Typography variant="caption" sx={{ color: isReturned ? 'rgba(255,255,255,0.3)' : '#10B981', fontWeight: 600 }}>
                                Returned: {checkinStr}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Chip
                            label={dev.type}
                            size="small"
                            sx={{
                              bgcolor: 'rgba(255,255,255,0.05)',
                              color: 'rgba(255,255,255,0.7)',
                              fontWeight: 700,
                              fontSize: '10px',
                              borderRadius: '4px',
                            }}
                          />
                          <Chip
                            label={isReturned ? 'RETURNED' : 'ASSIGNED'}
                            size="small"
                            sx={{
                              bgcolor: isReturned ? 'rgba(255,255,255,0.05)' : 'rgba(16, 185, 129, 0.1)',
                              color: isReturned ? 'rgba(255,255,255,0.4)' : '#10B981',
                              fontWeight: 700,
                              fontSize: '10px',
                              borderRadius: '4px',
                            // AI-GENERATED-end
                            }}
                          />
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ProjectDevicesDialog;
